import React, { useRef, useState } from "react";
import {
  ModalStyled,
  ModalBackgroundStyled,
} from "../../../../components/common/ModalComponent/styles";
import { releasesApi } from "../../api";
import {
  AdvancedLink,
  ClearFileButton,
  CloseButton,
  ComponentRowName,
  ComponentRowVersion,
  ComponentRow,
  ComponentTable,
  ComponentTypeBadge,
  DoneButton,
  DropHint,
  DropLink,
  DropText,
  DropZone,
  ErrorBlock,
  ErrorHint,
  ErrorList,
  ErrorTitle,
  ExampleLink,
  FileName,
  ModalBox,
  ModalSubtitle,
  ModalTitle,
  SelectedFile,
  StatusBadge,
  SummaryBlock,
  SummaryTitle,
  UploadButton,
} from "./styles";

const EXAMPLE_YAML = `# OTA Release Manifest — example
name: "release-mkii-v1.0.0"
vehicle_type: "MKII"
ccm_autonomy: "autonomy-env-tag"
ccm_config: "config-env-tag"
description: "Optional release description"

components:
  # Firmware component — firmware_key identifies the ECU slot in pending.yaml.
  # s3_uri, sha256_hash, and firmware_version are required only when the
  # version does not yet exist in the system.
  - name: "ecu_main"
    version: "1.2.3"
    firmware_key: "esp32c3"
    s3_uri: "s3://cyngn-deployment-ota/artifacts/com.cyngn.ecu_main/1.2.3/ecu_main.bin"
    sha256_hash: "a3f1c2d4e5b6..."
    firmware_version: "2.3.6.3"
    description: "Optional free-text note for this version"
    tags: ["stable", "june-sprint"]

  # Container component — ccm_image_tag is required only when the version
  # does not yet exist in the system.
  - name: "cynsrv_ouster_ros"
    version: "1.0.5"
    ccm_image_tag: "CS80d92ea3"
    ccm_service_name: "cynsrv_ouster_ros"
    description: "Optional free-text note for this version"
    tags: ["stable", "june-sprint"]

  # Host package component — host_package_version is required only when
  # the version does not yet exist in the system.
  - name: "cyngn-platform"
    version: "1.3.0"
    host_package_version: "1.3.0~rc7"
    description: "Optional free-text note for this version"
    tags: ["stable", "june-sprint"]
`;

function downloadExample() {
  const blob = new Blob([EXAMPLE_YAML], { type: "text/yaml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "release-manifest-example.yaml";
  a.click();
  URL.revokeObjectURL(url);
}

interface ContentProps {
  onSuccess: () => void;
  onAdvanced?: () => void;
}

interface ModalProps extends ContentProps {
  onClose: () => void;
}

const ACCEPTED = [".yaml", ".yml", ".json"];

function isAccepted(file: File): boolean {
  return ACCEPTED.some((ext) => file.name.toLowerCase().endsWith(ext));
}

/**
 * Shared upload logic and UI — no modal wrapper.
 * Used inline in DeploymentsView and inside ImportReleaseModal.
 */
export function ImportReleaseContent({ onSuccess, onAdvanced }: ContentProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<{
    message: string;
    hint?: string;
    missing?: { name: string; type: string }[];
    extra?: string[];
    details?: { name: string; error: string }[];
  } | null>(null);
  const [summary, setSummary] = useState<{
    releaseName: string;
    totalComponents: number;
    created: { name: string; versionNumber: string; type: string }[];
    reused: { name: string; versionNumber: string; type: string }[];
  } | null>(null);

  function selectFile(f: File) {
    if (!isAccepted(f)) {
      setError({ message: "Only .yaml, .yml, or .json files are accepted." });
      return;
    }
    setFile(f);
    setError(null);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(true);
  }

  function handleDragLeave() {
    setIsDragging(false);
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) selectFile(dropped);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = e.target.files?.[0];
    if (picked) selectFile(picked);
    e.target.value = "";
  }

  async function handleUpload() {
    if (!file || loading) return;
    setLoading(true);
    setError(null);
    try {
      const release = await releasesApi.importRelease(file);
      const s = (release as unknown as Record<string, unknown>).summary as
        | {
            totalComponents: number;
            created: { name: string; versionNumber: string; type: string }[];
            reused: { name: string; versionNumber: string; type: string }[];
          }
        | undefined;
      setSummary({
        releaseName: release.name,
        totalComponents: s?.totalComponents ?? 0,
        created: s?.created ?? [],
        reused: s?.reused ?? [],
      });
    } catch (err: unknown) {
      const data = (err as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      if (data) {
        setError({
          message: (data.error as string) || "An unexpected error occurred.",
          hint: data.hint as string | undefined,
          missing: data.missingComponents as unknown as
            | { name: string; type: string }[]
            | undefined,
          extra: data.extraComponents as string[] | undefined,
          details: data.details as
            | { name: string; error: string }[]
            | undefined,
        });
      } else {
        setError({ message: "Network error — please try again." });
      }
    } finally {
      setLoading(false);
    }
  }

  if (summary) {
    const allComponents = [
      ...summary.created.map((c) => ({ ...c, status: "created" as const })),
      ...summary.reused.map((c) => ({ ...c, status: "found" as const })),
    ];
    return (
      <>
        <ModalTitle>Release Created</ModalTitle>
        <SummaryBlock>
          <SummaryTitle>
            &quot;{summary.releaseName}&quot; — {summary.totalComponents}{" "}
            component(s)
          </SummaryTitle>
          <ComponentTable>
            {allComponents.map((c) => (
              <ComponentRow key={c.name}>
                <StatusBadge $status={c.status}>
                  {c.status === "created" ? "Created" : "Found"}
                </StatusBadge>
                <ComponentRowName>{c.name}</ComponentRowName>
                <ComponentRowVersion>v{c.versionNumber}</ComponentRowVersion>
                <ComponentTypeBadge $type={c.type}>{c.type}</ComponentTypeBadge>
              </ComponentRow>
            ))}
          </ComponentTable>
        </SummaryBlock>
        <DoneButton onClick={onSuccess}>Close</DoneButton>
      </>
    );
  }

  return (
    <>
      <div>
        <ModalTitle>Create Release from Manifest</ModalTitle>
        <ModalSubtitle>
          Upload a YAML or JSON file that defines all component versions for the
          release. The system will validate completeness against the vehicle
          type and create or reuse component versions automatically.{" "}
          <ExampleLink onClick={downloadExample}>Download example</ExampleLink>
        </ModalSubtitle>
      </div>

      {!file ? (
        <DropZone
          isDragging={isDragging}
          onClick={() => fileRef.current?.click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <DropText>
            Drop your manifest here, or <DropLink>click to browse</DropLink>
          </DropText>
          <DropHint>Supported formats: .yaml, .yml, .json</DropHint>
        </DropZone>
      ) : (
        <SelectedFile>
          <FileName>{file.name}</FileName>
          <ClearFileButton
            onClick={() => setFile(null)}
            aria-label="Remove file"
          >
            ×
          </ClearFileButton>
        </SelectedFile>
      )}

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPTED.join(",")}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {error && (
        <ErrorBlock>
          <ErrorTitle>{error.message}</ErrorTitle>
          {error.hint && <ErrorHint>{error.hint}</ErrorHint>}
          {error.missing && error.missing.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Missing from manifest ({error.missing.length}):
              </div>
              <ErrorList>
                {error.missing.map((c) => (
                  <li key={c.name}>
                    {c.name}
                    <ComponentTypeBadge $type={c.type}>
                      {c.type}
                    </ComponentTypeBadge>
                  </li>
                ))}
              </ErrorList>
            </>
          )}
          {error.extra && error.extra.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Not registered for this vehicle type ({error.extra.length}):
              </div>
              <ErrorList>
                {error.extra.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ErrorList>
            </>
          )}
          {error.details && error.details.length > 0 && (
            <>
              <div style={{ marginTop: 8, fontWeight: 500 }}>
                Component errors ({error.details.length}):
              </div>
              <ErrorList>
                {error.details.map((d, i) => (
                  <li key={i}>
                    <strong>{d.name}</strong>: {d.error}
                  </li>
                ))}
              </ErrorList>
            </>
          )}
        </ErrorBlock>
      )}

      <UploadButton onClick={handleUpload} disabled={!file || loading}>
        {loading ? "Creating release…" : "Upload & Create Release"}
      </UploadButton>

      {onAdvanced && (
        <AdvancedLink onClick={onAdvanced}>
          Need more control? Use the advanced wizard →
        </AdvancedLink>
      )}
    </>
  );
}

/**
 * Modal wrapper around ImportReleaseContent.
 * Used as the default "Create Release" entry point in ReleasesView.
 */
export function ImportReleaseModal({
  onClose,
  onSuccess,
  onAdvanced,
}: ModalProps) {
  return (
    <ModalStyled>
      <ModalBackgroundStyled onClick={onClose} />
      <ModalBox>
        <CloseButton onClick={onClose} aria-label="Close">
          ×
        </CloseButton>
        <ImportReleaseContent onSuccess={onSuccess} onAdvanced={onAdvanced} />
      </ModalBox>
    </ModalStyled>
  );
}
